import { createLazyFileRoute, useSearch } from "@tanstack/react-router";
import { memo } from "react";
import { useTitle } from "react-use";

import { CommandMenu, CommandMenuProvider } from "@/components/CommandMenu";
import { ErrorPage } from "@/components/ErrorPage";
import { PermissionGate } from "@/components/PermissionGate";
import { RoleCondition } from "@/components/RoleCondition";
import { SubHeader } from "@/components/SubHeader";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Subject } from "@/features/subjects/components/Subject";
import { SubjectCommands } from "@/features/subjects/components/SubjectCommands";
import { SubjectDropdown } from "@/features/subjects/components/SubjectDropdown";
import { SubjectProvider } from "@/features/subjects/components/SubjectProvider";
import { NavStackProvider, useInitializeNavStack } from "@/utils/useNavStack";

export const Route = createLazyFileRoute("/_auth/_app/subjects/$id")({
  component: memo(SubjectDetails),
  errorComponent: (err) => {
    if (err.error instanceof Error) {
      return <ErrorPage error={err.error} />;
    }
    return <ErrorPage error={{ message: "An unknown error occurred" }} />;
  },
});

type ChildSearchUnsafe = { accessionId?: number; reportId?: number };

function SubjectDetails() {
  const { subject } = Route.useLoaderData();
  const { accessionId, reportId } = useSearch({
    strict: false,
  }) as ChildSearchUnsafe;

  useInitializeNavStack();
  useTitle(`Donor - ${subject.caseId}`);

  return (
    <NavStackProvider label="Donors" href={{ to: "/accessions" }}>
      <CommandMenuProvider>
        <SubjectProvider subject={subject}>
          <SubHeader>
            <SubHeader.Left>
              <Breadcrumbs>
                <Breadcrumb href={{ to: "/accessions" }}>Donors</Breadcrumb>
                <Breadcrumb>{subject.caseId}</Breadcrumb>
              </Breadcrumbs>
            </SubHeader.Left>
            <PermissionGate requiredPermission="update:subjects">
              <SubHeader.Right className="gap-2">
                {/* <Button>
                  <PlusIcon />
                  New accession
                </Button> */}
                <SubjectDropdown subject={subject} />
              </SubHeader.Right>
            </PermissionGate>
          </SubHeader>
          {!accessionId && !reportId && (
            <CommandMenu>
              <SubjectCommands />
            </CommandMenu>
          )}
          <SidebarProvider>
            <Subject subject={subject} />
          </SidebarProvider>
        </SubjectProvider>
      </CommandMenuProvider>
    </NavStackProvider>
  );
}
